import { useEffect, useState } from 'react';
import { Database, Layers, AlertTriangle, Activity, Shield } from 'lucide-react';

interface Pool {
  id: string;
  name: string;
  provider: string;
  health: 'online' | 'degraded' | 'faulted' | 'offline';
  rawBytes: number;
  usableBytes: number;
  usedBytes: number;
  freeBytes: number;
  fragmentation: number;
  compressionRatio: number;
  dedupRatio: number;
  drives: number;
  redundancy: string;
  errors: number;
  scrubStatus: string;
  rebuildProgress: number | null;
}

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(2)} ${units[i]}`;
};

const healthBadge = (health: string) => {
  const map: Record<string, string> = {
    online: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    degraded: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    faulted: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
    offline: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };
  return map[health] || map.online;
};

export default function Pools() {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/pools', { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        setPools(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading pools...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
        <Database size={24} /> Storage Pools
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {pools.map((pool) => (
          <div key={pool.id} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Layers size={18} /> {pool.name}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{pool.provider.toUpperCase()} • {pool.redundancy} • {pool.drives} drives</div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${healthBadge(pool.health)}`}>{pool.health}</span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{formatBytes(pool.usedBytes)} used</span>
                <span className="text-slate-500 dark:text-slate-400">{formatBytes(pool.usableBytes)} total</span>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full"
                  style={{ width: `${Math.min(100, (pool.usedBytes / pool.usableBytes) * 100)}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <MiniStat label="Free" value={formatBytes(pool.freeBytes)} />
              <MiniStat label="Compression" value={`${pool.compressionRatio}x`} />
              <MiniStat label="Fragmentation" value={`${pool.fragmentation}%`} />
              <MiniStat label="Errors" value={pool.errors.toString()} />
            </div>

            <div className="flex items-center gap-2 text-xs">
              {pool.health === 'degraded' && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertTriangle size={14} /> Pool is degraded
                </span>
              )}
              <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                <Shield size={14} /> Scrub: {pool.scrubStatus}
              </span>
              {pool.rebuildProgress !== null && (
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Activity size={14} /> Rebuild {pool.rebuildProgress}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 dark:bg-slate-800/50 p-2">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-semibold text-slate-900 dark:text-white">{value}</div>
    </div>
  );
}
